import { AdfsClientCredential } from '@app/core/models/adfs-client-credential.model';
import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Observable,
  retry,
  Observer,
  switchMap,
  map,
  of,
  tap,
  noop,
  debounceTime,
  distinctUntilChanged,
  timeout,
} from 'rxjs';
import { APP_CONFIG, AppConfig } from '@app/app-config.module';
import { StandardResponse, StandardResponseList, StandardResponses } from '@app/core/models/standard-response.model';
import { UserManagementCompany, UserManagementCompanyType, UserManagementStandardResponse } from '@app/core/models/user-management-company.model';
import { SearchUserManagementCompany } from '@app/core/models/search-user-management-company.model';
import { SearchCompanyReport } from '../models/search-company_report.model';
import { UserSearchResult } from '../models/user.model';
import { FormControl } from '@angular/forms';
import { DeleteUserRequest } from '../models/user-config.model';
import { ICompany_V2, SearchCompany } from '../models/company.model';
import { IPagination, IResponse } from 'usermanagement-boothelp/src/app/shared/models/response.model';

interface ObservedEmailSource {
  email: string,
  valid: boolean
}

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  constructor(
    @Inject(APP_CONFIG) private config: AppConfig,
    private http: HttpClient
  ) { }

  getCompanies(
    criteria: SearchUserManagementCompany,
    pageNumber: number,
    pageSize: number
  ): Observable<StandardResponses<UserManagementCompany>> {
    return this.http
      .post<StandardResponses<UserManagementCompany>>(
        `${this.config.apiEndpoint}/user-management/company`,
        { criteria: criteria, pageNumber: pageNumber, pageSize: pageSize }
      )
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  getCompanyTypes(): Observable<UserManagementStandardResponse<UserManagementCompanyType[]>> {
    return this.http
      .post<UserManagementStandardResponse<UserManagementCompanyType[]>>(
        `${this.config.apiEndpoint}/users/getlistcompanytype`, {}
      )
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  searchJuristicIndividualCompanies(
    criteria: SearchCompanyReport,
    pageNumber: number,
    pageSize: number
  ): Observable<StandardResponses<UserManagementCompany>> {
    return this.http
      .post<StandardResponses<UserManagementCompany>>(
        `${this.config.apiEndpoint}/user-management/search-juristic-individual-company`,
        { criteria: criteria, pageNumber: pageNumber, pageSize: pageSize }
      )
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  getListPermission(
  ): Observable<StandardResponseList<string>> {
    return this.http
      .post<StandardResponseList<string>>(
        `${this.config.apiEndpoint}/tab-user-profile/get-list-permission`,
        {}
      )
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  checkRequireChangePassword(

  ): Observable<StandardResponse<string>> {
    return this.http
      .post<StandardResponse<string>>(
        `${this.config.apiEndpoint}/user-management/required-change-password`,
        {}
      ).pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  getAllUsersByEmail(email: string): Observable<UserSearchResult> {
    return this.http
      .post<UserSearchResult>(`${this.config.apiEndpoint}/info/search-user-info`, {
        pageSize: 20,
        pageNum: 1,
        email: email,
      })
      .pipe(retry(this.config.apiRetry));
  }

  getAllUsersWithDefaultFirstByEmailAsObservable(participant: FormControl, defaultUid: string) {
    return new Observable((observer: Observer<ObservedEmailSource | undefined>) => {
      observer.next({email:participant.value, valid:participant.valid});
    }).pipe(
      debounceTime(500),
      distinctUntilChanged((prev, cur) => prev.email === cur.email),
      switchMap((emailSrc: ObservedEmailSource) => {
        if (emailSrc && emailSrc.email) {
          return this.getAllUsersByEmail(emailSrc.email).pipe(
            map((response: UserSearchResult) => {
              if(response && response.status == "success" && response.data && response.data.data){
                if(emailSrc.valid){
                  return [{
                    active: true,
                    approvalId: "",
                    approve: false,
                    company: "",
                    email: emailSrc.email,
                    firstName: "",
                    group: "",
                    lastName: "",
                    lock: false,
                    loginFail: 0,
                    uid: defaultUid,
                    username: ""
                  }, ...response.data.data];
                }else{
                  return response.data.data;
                }
              }else{
                return [];
              }
            }),
            tap(
              () => noop,
              (err) => {
                console.log((err && err.message) || 'Something went wrong');
              }
            )
          );
        }
        return of([]);
      })
    );
  }

  deleteUser(data: DeleteUserRequest): Observable<StandardResponse<string>> {
    return this.http
      .post<any>(`${this.config.apiEndpoint}/tab-user-profile/deleted-user-under-company`, data)
      .pipe(timeout(this.config.apiTimeout), retry(this.config.apiRetry));
  }

  cancelDeleteUser(data: DeleteUserRequest): Observable<StandardResponse<string>> {
    return this.http
      .post<any>(`${this.config.apiEndpoint}/tab-user-profile/cancel-deleted-user-under-company`, data)
      .pipe(timeout(this.config.apiTimeout), retry(this.config.apiRetry));
  }

  listDeleteAllUserUnderComp(): Observable<StandardResponse<any>> {
    return this.http
      .post<any>(`${this.config.apiEndpoint}/tab-user-profile/list-deleted-all-user-under-company`, {})
      .pipe(timeout(this.config.apiTimeout), retry(this.config.apiRetry));
  }

  searchCompany_V2(
    pageNum: number,
    pageSize: number,
    companySearch: SearchCompany
  ): Observable<IResponse<IPagination<ICompany_V2>>> {
    return this.http
      .post<IResponse<IPagination<ICompany_V2>>>(`${this.config.apiEndpoint}/users/v2/getlistcompany`, {
        pageNum: pageNum,
        pageSize: pageSize,
        ...companySearch,
      })
      .pipe(timeout(this.config.apiTimeout), retry(this.config.apiRetry));
  }
}
